import { createFileRoute } from '@tanstack/react-router'
import {operators} from '@/constants/operators'
import UserFilter from '@/components/user-filter'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {type ColumnDef} from '@tanstack/react-table'
import {ChevronDown, ChevronLeft} from 'lucide-react'
import {useState} from 'react'
import NumberWithHoverPoints from '@/components/NumberWithHoverPoint'
import { ProductTypes } from '@/types'
import { DataTable } from '@/components/ui/data-table'

export const Route = createFileRoute('/client/sale/report/')({
  component: SaleReport,
})

function SaleReport() {
  const [filtered, setFiltered] = useState<any>(null)
  const [meta, setMeta] = useState({})

  const baseColumns: ColumnDef<{
    count: number
    sum: number
    sum000value: number
    count000value: number
    sum000original_price: number
    operator_id: number
    value: number
    product_type: keyof typeof ProductTypes
    product_name: string
  }>[] = [
      {
        id: 'expander',
        header: () => null,
        cell: ({ row }) => row.getCanExpand() ? (
          <button onClick={() => row.toggleExpanded()}>
            {row.getIsExpanded() ? <ChevronDown /> : <ChevronLeft />}
          </button>
        ) : null,
        footer: () => {
          return 'مجموع '
        },
      },
      {
        header: 'اپراتور',
        accessorKey: 'operator_id',
        cell({ getValue }) {
          return operators.find((op) => op.id === getValue())?.title
        },
      },
      {
        header: 'نوع محصول',
        accessorKey: 'product_type',
        cell: ({ row }) => {
          return <>{ProductTypes[row.original.product_type]}</>
        },
      },
      {
        header: 'محصول',
        accessorKey: 'product_name',
        cell(props) {

          if (!props.row.original.product_name) {
            return ''
          }

          if (props.row.original.product_type === 'td_lte_internet') {
            return `${props.getValue()}`.replace('- دائمی', '')
          }
          
          return props.getValue()
        },
      },
      {
        header: 'تعداد',
        accessorKey: 'count000value',
        cell(props) {
          return Intl.NumberFormat('fa-ir').format(props.row.original['count000value'])
        },
        footer: (info) => {
          // Sum the column values
          const total = info.table
            .getRowModel()
            .rows.filter(d =>d.parentId == undefined).reduce((sum, row) => sum + row.original.count000value, 0)
          return `${total}`
        },
      },
      {
        header: 'مجموع برداشت',
        accessorKey: 'sum000value',
        cell(props) {
          return <NumberWithHoverPoints number={props.row.original['sum000value']} />
        },
        footer: (info) => {
          // Sum the column values
          const total = info.table
            .getRowModel()
            .rows?.filter(d =>d.parentId == undefined).reduce((sum, row) => sum + Number(row.getValue('sum000value')), 0)
          return <NumberWithHoverPoints number={total} />
        },
      },
      {
        header: 'مجموع واقعی',
        accessorKey: 'sum000original_price',
        cell(props) {
          return <NumberWithHoverPoints number={props.row.original['sum000original_price']} />
        },
        footer: (info) => {
          // Sum the column values
          const total = info.table
            .getRowModel()
            .rows?.filter(d =>d.parentId == undefined).reduce((sum, row) => sum + Number(row.original.sum000original_price), 0)
          return <NumberWithHoverPoints number={total} />
        },
      },
      {
        header: 'سود',
        id: 'profit',
        cell(props) {
          return (
            <NumberWithHoverPoints
              number={Number(
                props.row.original.sum000original_price - props.row.original.sum000value
              )} />
          )
        },
        footer: (info) => {
          // Sum the column values
          const totalOriginalPrice = info.table
            .getRowModel()
            .rows?.filter(d =>d.parentId == undefined).reduce((sum, row) => sum + Number(row.original.sum000original_price), 0)
          const totalValue = info.table
            .getRowModel()
            .rows?.filter(d =>d.parentId == undefined).reduce((sum, row) => sum + Number(row.original.sum000value), 0)
          console.log(totalOriginalPrice, totalValue)
          const total = totalOriginalPrice - totalValue
          return <NumberWithHoverPoints number={total} />
        },
      },
    ]

  function groupByProductType(data: Array<any>) {
    // ابتدا داده‌ها را بر اساس product_type گروه‌بندی می‌کنیم
    const groupedData = data.reduce((acc, item) => {
      const { product_type, sum000value, count000value, sum000original_price } = item

      // اگر گروهی با این product_type وجود ندارد، ایجادش کن
      if (!acc[product_type]) {
        acc[product_type] = {
          subRows: [],
          sum000value: 0,
          count000value: 0,
          sum000original_price: 0,
        }
      }

      // آیتم را به گروه مربوطه اضافه کن
      acc[product_type].subRows.push(item)

      // جمع sum000value و count000value را به‌روز کن
      acc[product_type].sum000value += parseFloat(sum000value) || 0
      acc[product_type].count000value += parseInt(count000value) || 0
      acc[product_type].sum000original_price += parseInt(sum000original_price) || 0

      return acc
    }, {})

    // داده‌ها را به فرمت نهایی تبدیل می‌کنیم
    const result: Array<any> = Object.keys(groupedData).map((productType) => {
      const group = groupedData[productType]
      return {
        product_type: productType,
        sum000value: group.sum000value,
        count000value: group.count000value,
        sum000original_price: group.sum000original_price,
        subRows: group.subRows,
      }
    })

    return result
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>جزئیات فروش</CardTitle>
        </CardHeader>

        <CardContent>
          <UserFilter
            table='walletTransaction'
            filters={[]}
            setFiltered={(filtered) => {
              if (filtered !== null &&
                filtered.filter((d: any) => d.product_type != null).length > 0) {
                setFiltered(groupByProductType(filtered))
              } else if (filtered !== null) {
                setFiltered(filtered)
              } else {
                setFiltered(null)
              }
            } }
            filtered={filtered}
            setMeta={setMeta}
            groupBy={['operator_id,product_type,product_name', 'operator_id','product_name,operator_id']} />
          {filtered && (
            <DataTable<{
              count: number
              sum: number
              sum000value: number
              count000value: number
              sum000original_price: number
              operator_id: number
              value: number
              product_type: keyof typeof ProductTypes
              product_name: string
            }, any>
              enablePagination={false}
              columns={baseColumns}
              data={filtered}
              pageCount={1}
               />
          )}
        </CardContent>
      </Card>
    </>
  )
}
